import { mode } from '@chakra-ui/theme-tools';
import type { Aesthetic } from './aesthetics';

/** Convert hex color to rgba string */
function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Build global styles with aesthetic-specific neutral surfaces and semantics.
 * Called by buildTheme() — the aesthetic controls surfaces, borders, text colors,
 * shadows, and semantic status colors.
 */
export function buildGlobalStyles(aesthetic: Aesthetic) {
	return {
		colors: {
			brand: {
				50: '#EBF2FA', 100: '#C8DCF0', 200: '#A5C6E6', 300: '#6D9FD3',
				400: '#3578BF', 500: '#023F7F', 600: '#023569', 700: '#012B54',
				800: '#01213F', 900: '#01172A',
			},
			brandScheme: {
				50: '#EBF2FA', 100: '#C8DCF0', 200: '#A5C6E6', 300: '#6D9FD3',
				400: '#3578BF', 500: '#023F7F', 600: '#023569', 700: '#012B54',
				800: '#01213F', 900: '#01172A',
			},
			secondaryGray: {
				100: aesthetic.surfaces.border,
				200: aesthetic.surfaces.border,
				300: aesthetic.surfaces.pageBg,
				400: aesthetic.surfaces.borderSubtle,
				500: aesthetic.surfaces.textSecondary,
				600: aesthetic.surfaces.textMuted,
				700: aesthetic.surfaces.textSecondary,
				800: aesthetic.surfaces.textSecondary,
				900: aesthetic.surfaces.textPrimary,
			},
			red: { 100: '#FEEFEE', 500: '#EE5D50', 600: '#E31A1A' },
			blue: { 50: '#EFF4FB', 500: '#023F7F' },
			orange: { 100: '#FFF6DA', 500: '#FFB547' },
			green: { 100: '#E6FAF5', 500: '#01B574' },
			navy: {
				50: '#d0dcfb', 100: '#aac0fe', 200: '#a3b9f8', 300: '#728fea',
				400: '#3652ba', 500: '#1b3bbb', 600: '#24388a',
				700: aesthetic.darkSurfaces['700'],
				800: aesthetic.darkSurfaces['800'],
				900: aesthetic.darkSurfaces['900'],
			},
			gray: { 100: '#FAFCFE' },
			accent: {
				50: '#ECFDF5', 100: '#D1FAE5', 200: '#A7F3D0', 300: '#6EE7B7',
				400: '#34D399', 500: '#10B981', 600: '#059669', 700: '#047857',
				800: '#065F46', 900: '#064E3B',
			},
			success: { 50: '#ECFDF5', 100: '#D1FAE5', 500: '#10B981', 600: '#059669' },
			warning: aesthetic.semantic.warning,
			danger: aesthetic.semantic.danger,
			info: aesthetic.semantic.info,
		},
		shadows: {
			sm: `0px 1px 3px ${aesthetic.shadowBase} 0.06), 0px 1px 2px ${aesthetic.shadowBase} 0.04)`,
			md: `0px 2px 8px ${aesthetic.shadowBase} 0.08), 0px 1px 3px ${aesthetic.shadowBase} 0.04)`,
			lg: `0px 4px 16px ${aesthetic.shadowBase} 0.10), 0px 2px 6px ${aesthetic.shadowBase} 0.04)`,
			xl: `0px 8px 32px ${aesthetic.shadowBase} 0.12), 0px 4px 12px ${aesthetic.shadowBase} 0.04)`,
			brand: '0px 4px 16px rgba(2, 63, 127, 0.20), 0px 2px 6px rgba(2, 63, 127, 0.10)',
			'brand-lg': '0px 8px 32px rgba(2, 63, 127, 0.25), 0px 4px 12px rgba(2, 63, 127, 0.12)',
		},
		styles: {
			global: (props: any) => {
				const headingFont = aesthetic.useDisplaySerif
					? '"Urbanist", "Figtree", sans-serif'
					: '"Urbanist", "Figtree", sans-serif';
				const displayFont = aesthetic.useDisplaySerif
					? '"Instrument Serif", "Urbanist", serif'
					: '"Urbanist", "Figtree", sans-serif';
				const monoFont = aesthetic.useMonoData
					? '"JetBrains Mono", "Fira Code", monospace'
					: 'inherit';

				return {
					body: {
						overflowX: 'hidden',
						bg: mode('secondaryGray.300', 'navy.900')(props),
						fontFamily: '"Figtree", sans-serif',
						letterSpacing: '-0.01em',
						WebkitFontSmoothing: 'antialiased',
						MozOsxFontSmoothing: 'grayscale',
					},
					input: { color: 'gray.700' },
					html: {
						fontFamily: '"Figtree", sans-serif',
						// Expose aesthetic fonts as CSS custom properties for components
						'--display-font': displayFont,
						'--mono-font': monoFont,
						'--heading-font': headingFont,
						// Navbar backdrop colors (page bg at low opacity)
						'--navbar-bg-light': hexToRgba(aesthetic.surfaces.pageBg, 0.2),
						'--navbar-bg-dark': hexToRgba(aesthetic.darkSurfaces['900'], 0.5),
					},
					'h1, h2, h3, h4, h5, h6': {
						fontFamily: headingFont,
					},
					// h1 gets the display serif when Clubhouse is active
					h1: aesthetic.useDisplaySerif ? {
						fontFamily: displayFont,
						letterSpacing: '-0.02em',
					} : {},
					// Utility class: apply mono font to any element
					'.mono-data': {
						fontFamily: monoFont,
						fontFeatureSettings: '"tnum" 1',
					},
					// Tabular numerals in all table cells for aligned columns
					'td, th': {
						fontVariantNumeric: 'tabular-nums',
					},
					// Focus-visible ring: 3px brand-colored, 2px offset
					'*:focus-visible': {
						outline: '3px solid',
						outlineColor: mode('brand.400', 'brand.300')(props),
						outlineOffset: '2px',
					},
					// Remove default focus outline (focus-visible handles keyboard nav)
					'*:focus:not(:focus-visible)': {
						outline: 'none',
					},
					// Sidebar nav links span the full sidebar width — the universal
					// focus ring (outward or inset) renders as a horizontal bar
					// across the top/bottom of the link. The active-route state
					// already provides a clear visual indicator, so suppress all
					// browser-default focus/active borders here.
					'.sidebar-nav-link, .sidebar-nav-link:hover, .sidebar-nav-link:focus, .sidebar-nav-link:focus-visible, .sidebar-nav-link:active': {
						outline: 'none !important',
						boxShadow: 'none !important',
						border: 'none !important',
						textDecoration: 'none !important',
					},
					// react-md-editor overlays a content-sized <textarea> on top of a <pre>;
					// the universal focus ring above renders around the tiny textarea and
					// shows up as a stray blue underline mid-pane. The editor wrapper gets
					// its own focus styling, so suppress the inner ring.
					'.w-md-editor *:focus-visible': {
						outline: 'none',
					},
					// Reduced motion: dampen decorative transitions/animations, but
					// EXEMPT indefinite loading indicators (Chakra Spinner + Skeleton
					// shimmer). Clamping animation-iteration-count to 1 + duration to
					// 0.01ms froze the spinner after a single near-instant rotation,
					// so it rendered but never visibly span — the app looked hung.
					// Loading spinners are essential motion (WCAG 2.3.3 exempts
					// activity indicators), so they must keep animating.
					'@media (prefers-reduced-motion: reduce)': {
						'*:not(.chakra-spinner):not(.chakra-skeleton), *::before, *::after': {
							animationDuration: '0.01ms !important',
							animationIterationCount: '1 !important',
							transitionDuration: '0.01ms !important',
							scrollBehavior: 'auto !important',
						},
					},
				};
			},
		},
	};
}

/** @deprecated Use buildGlobalStyles(aesthetic) instead. Kept for backwards compatibility. */
export const globalStyles = {
	colors: {
		brand: {
			50: '#EBF2FA',
			100: '#C8DCF0',
			200: '#A5C6E6',
			300: '#6D9FD3',
			400: '#3578BF',
			500: '#023F7F',
			600: '#023569',
			700: '#012B54',
			800: '#01213F',
			900: '#01172A'
		},
		// brandScheme is used by legacy Horizon UI template views and SwitchField.
		// It's intentionally identical to brand — prefer using 'brand' for new code.
		brandScheme: {
			50: '#EBF2FA',
			100: '#C8DCF0',
			200: '#A5C6E6',
			300: '#6D9FD3',
			400: '#3578BF',
			500: '#023F7F',
			600: '#023569',
			700: '#012B54',
			800: '#01213F',
			900: '#01172A'
		},
		// Neutral gray scale — blue tint removed (was Horizon UI artifact).
		// Note: scale is NOT monotonic (300 is lightest = page bg, 100 = hover bg).
		secondaryGray: {
			100: '#E5E7EB',
			200: '#E5E7EB',
			300: '#F4F5F7',
			400: '#EBEDF0',
			500: '#6B7280',
			600: '#9CA3AF',
			700: '#6B7280',
			800: '#6B7280',
			900: '#1A1D26'
		},
		red: {
			100: '#FEEFEE',
			500: '#EE5D50',
			600: '#E31A1A'
		},
		blue: {
			50: '#EFF4FB',
			500: '#023F7F'
		},
		orange: {
			100: '#FFF6DA',
			500: '#FFB547'
		},
		green: {
			100: '#E6FAF5',
			500: '#01B574'
		},
		// 50–600: decorative brand-adjacent blues (unchanged).
		// 700–900: dark mode surface backgrounds — zinc scale for true-dark UI.
		navy: {
			50: '#d0dcfb',
			100: '#aac0fe',
			200: '#a3b9f8',
			300: '#728fea',
			400: '#3652ba',
			500: '#1b3bbb',
			600: '#24388a',
			700: '#27272A',
			800: '#18181B',
			900: '#09090B'
		},
		gray: {
			100: '#FAFCFE'
		},
		accent: {
			50: '#ECFDF5',
			100: '#D1FAE5',
			200: '#A7F3D0',
			300: '#6EE7B7',
			400: '#34D399',
			500: '#10B981',
			600: '#059669',
			700: '#047857',
			800: '#065F46',
			900: '#064E3B',
		},
		// Semantic status colors with tints
		success: {
			50: '#ECFDF5',
			100: '#D1FAE5',
			500: '#10B981',
			600: '#059669',
		},
		warning: {
			50: '#FFFBEB',
			100: '#FEF3C7',
			500: '#F59E0B',
			600: '#D97706',
		},
		danger: {
			50: '#FEF2F2',
			100: '#FEE2E2',
			500: '#EF4444',
			600: '#DC2626',
		},
		info: {
			50: '#EBF5FF',
			100: '#D6EAFF',
			500: '#3B82F6',
			600: '#2563EB',
		},
	},
	shadows: {
		sm: '0px 1px 3px rgba(16, 24, 40, 0.06), 0px 1px 2px rgba(16, 24, 40, 0.04)',
		md: '0px 2px 8px rgba(16, 24, 40, 0.08), 0px 1px 3px rgba(16, 24, 40, 0.04)',
		lg: '0px 4px 16px rgba(16, 24, 40, 0.10), 0px 2px 6px rgba(16, 24, 40, 0.04)',
		xl: '0px 8px 32px rgba(16, 24, 40, 0.12), 0px 4px 12px rgba(16, 24, 40, 0.04)',
		// Brand-tinted shadows for elevated CTA elements
		brand: '0px 4px 16px rgba(2, 63, 127, 0.20), 0px 2px 6px rgba(2, 63, 127, 0.10)',
		'brand-lg': '0px 8px 32px rgba(2, 63, 127, 0.25), 0px 4px 12px rgba(2, 63, 127, 0.12)',
	},
	styles: {
		global: (props: any) => ({
			body: {
				overflowX: 'hidden',
				bg: mode('secondaryGray.300', 'navy.900')(props),
				fontFamily: '"Figtree", sans-serif',
				letterSpacing: '-0.01em',
				WebkitFontSmoothing: 'antialiased',
				MozOsxFontSmoothing: 'grayscale',
			},
			input: {
				color: 'gray.700'
			},
			html: {
				fontFamily: '"Figtree", sans-serif'
			},
			'h1, h2, h3, h4, h5, h6': {
				fontFamily: '"Urbanist", "Figtree", sans-serif',
			}
		})
	}
};
